(() => {
  'use strict';

  const qsa = (root, selector) => Array.from(root?.querySelectorAll?.(selector) || []);
  const wait = (callback, delay) => window.setTimeout(callback, delay);

  class AVMotionCraft {
    constructor() {
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.intensity = document.body?.dataset.motionIntensity || 'subtle';
      this.enabled = document.body?.dataset.motionEnabled !== 'false' && this.intensity !== 'off';
      this.revealObserver = null;
      this.heroObserver = null;
      this.heroAnimations = new Map();
      this.sectionRoots = new Map();
      this.mediaToken = 0;
      this.cartToken = 0;
      this.cartOriginalLabels = new WeakMap();
      this.cart = this.createCartMachine();
      this.live = this.createLiveRegion();
      this.bindGlobalEvents();
      this.init(document);
    }

    createLiveRegion() {
      const existing = document.querySelector('[data-motion-live]');
      if (existing) return existing;
      const live = document.createElement('div');
      live.className = 'av-motion-live';
      live.dataset.motionLive = '';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
      return live;
    }

    announce(message) {
      this.live.textContent = '';
      requestAnimationFrame(() => { this.live.textContent = message; });
    }

    init(root = document) {
      this.annotate(root);
      this.syncHorizonState(root);
      document.documentElement.dataset.motionReady = 'true';
      document.documentElement.dataset.avMotionReady = 'true';
      if (!this.enabled || this.reduced.matches) {
        this.finishAll(root);
        return;
      }
      this.observeReveals(root);
      this.observeHeroes(root);
      this.initIngredientToggles(root);
    }

    annotate(root) {
      const scope = root?.querySelectorAll ? root : document;
      qsa(scope, '[data-av-reveal],[data-av-v15-reveal]').forEach((element) => {
        if (!element.dataset.motionReveal) element.dataset.motionReveal = element.dataset.avReveal || 'up';
      });
      qsa(scope, '.av-ingredient-motion__item').forEach((element) => {
        element.dataset.motionReveal ||= 'up';
      });
      qsa(scope, '.av-founder-craft__cards,.av-favorites__grid,.av15-pdp__panels,.av15-story-grid,[data-motion-reveal-group]').forEach((group) => {
        Array.from(group.children).slice(0, 6).forEach((item, index) => {
          item.dataset.motionReveal ||= 'up';
          item.style.setProperty('--av-motion-delay', `${Math.min(index * 55, 275)}ms`);
        });
      });
      qsa(scope, '[data-av-card11],.avc14-card,.av-favorites-card,.product-grid-item').forEach((card) => {
        card.dataset.motionCard = '';
        card.dataset.motionReveal ||= 'up';
        const siblingIndex = Array.from(card.parentElement?.children || []).indexOf(card);
        card.style.setProperty('--av-motion-delay', `${Math.min(Math.max(siblingIndex, 0) % 6 * 55, 275)}ms`);
        const media = card.querySelector('.av-card11__stage,.avc14-card__media,.product-item__image');
        if (media) media.dataset.motionCardMedia = '';
      });
      qsa(scope, 'button[data-add-to-cart],button[data-av-card11-submit],.av-card11__button').forEach((button) => {
        button.dataset.motionPress = '';
        this.rememberCartLabel(button);
      });
      qsa(scope, 'product-images').forEach((gallery) => { gallery.dataset.motionMedia = ''; });
      qsa(scope, '.cart__item').forEach((line) => { line.dataset.motionCartLine ||= ''; });
    }

    syncHorizonState(root, forceStatic = false) {
      const body = document.body;
      const globalGate = body?.dataset.avV112 === 'true'
        && body.dataset.avV112Horizon === 'true'
        && body.dataset.avV112HorizonMotion === 'true';
      qsa(root, '[data-au-horizon]').forEach((horizon) => {
        const finite = !forceStatic
          && globalGate
          && this.enabled
          && !this.reduced.matches
          && horizon.dataset.auHorizonMotion === 'finite';
        horizon.dataset.motionHorizonState = finite ? 'finite' : 'static';
      });
    }

    finishAll(root) {
      qsa(root, '[data-motion-reveal]').forEach((element) => { element.dataset.motionState = 'done'; });
      this.syncHorizonState(root, true);
      this.heroAnimations.forEach((animation) => animation.cancel());
      this.heroAnimations.clear();
    }

    observeReveals(root) {
      if (!('IntersectionObserver' in window)) return this.finishAll(root);
      this.revealObserver ||= new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const element = entry.target;
          element.dataset.motionState = 'active';
          this.revealObserver.unobserve(element);
          const complete = () => {
            element.dataset.motionState = 'done';
            element.removeEventListener('transitionend', complete);
          };
          element.addEventListener('transitionend', complete, { once: true });
          wait(complete, 700);
        });
      }, { threshold: .05, rootMargin: '0px 0px -2% 0px' });
      qsa(root, '[data-motion-reveal]:not([data-motion-state="done"])').forEach((element) => {
        const bounds = element.getBoundingClientRect();
        if (bounds.top < window.innerHeight * .98 && bounds.bottom > 0) {
          element.dataset.motionState = 'done';
          return;
        }
        element.dataset.motionState = 'pending';
        this.revealObserver.observe(element);
      });
    }

    observeHeroes(root) {
      if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;
      this.heroObserver ||= new IntersectionObserver((entries) => entries.forEach((entry) => {
        const hero = entry.target;
        if (entry.isIntersecting && !document.hidden) this.startHero(hero);
        else this.heroAnimations.get(hero)?.pause();
      }), { threshold: .18 });
      qsa(root, '[data-motion-hero]').forEach((hero) => this.heroObserver.observe(hero));
    }

    startHero(hero) {
      const existing = this.heroAnimations.get(hero);
      if (existing) return existing.playState === 'finished' ? undefined : existing.play();
      const layer = hero.querySelector('[data-motion-hero-layer]');
      if (!layer) return;
      const run = () => {
        if (this.reduced.matches || !this.enabled) return;
        const direction = hero.dataset.motionDirection === 'right' ? 1 : -1;
        const scale = this.intensity === 'standard' ? 1.032 : 1.024;
        const animation = layer.animate([
          { transform: 'scale(1.015) translate3d(0,0,0)' },
          { transform: `scale(${scale}) translate3d(${direction * .55}%, -.35%, 0)` }
        ], { duration: 18000, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards', iterations: 1 });
        this.heroAnimations.set(hero, animation);
      };
      if (layer.tagName === 'IMG' && layer.decode) layer.decode().catch(() => {}).finally(run);
      else run();
    }

    initIngredientToggles(root) {
      qsa(root, '.av-ingredient-motion__toggle:not([data-motion-toggle-ready])').forEach((button) => {
        button.dataset.motionToggleReady = 'true';
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!expanded));
          button.closest('.av-ingredient-motion__item')?.classList.toggle('is-open', !expanded);
        });
      });
    }

    onMediaSelect(event) {
      const gallery = event.target.closest?.('product-images') || event.target.querySelector?.('product-images');
      if (!gallery || this.reduced.matches) return;
      const token = ++this.mediaToken;
      gallery.dataset.motionMediaSwitching = 'true';
      wait(() => {
        if (token === this.mediaToken) delete gallery.dataset.motionMediaSwitching;
      }, 360);
    }

    rememberCartLabel(button, force = false) {
      const label = button?.querySelector('[data-add-to-cart-text],[data-av-card11-button-text],.btn__text');
      if (!label) return;
      const state = button.dataset.motionCartState;
      const transient = /adicionando|adicionado|tente novamente/i.test(label.textContent || '');
      if (button.getAttribute('aria-busy') === 'true' || state === 'loading' || state === 'success' || state === 'error' || transient) return;
      if (force || !this.cartOriginalLabels.has(button)) {
        this.cartOriginalLabels.set(button, { html: label.innerHTML, text: label.textContent?.trim() || '' });
      }
    }

    createCartMachine() {
      const state = { current: null, pendingVariant: '', previousCount: null };
      const labelNode = (button) => button?.querySelector('[data-add-to-cart-text],[data-av-card11-button-text],.btn__text');
      const clearTimer = (entry) => {
        if (entry?.timer) {
          clearTimeout(entry.timer);
          entry.timer = null;
        }
      };
      const reset = (entry, delay = 0) => wait(() => {
        clearTimer(entry);
        if (!entry?.button?.isConnected) {
          if (state.current === entry) state.current = null;
          return;
        }
        entry.button.dataset.motionCartState = 'idle';
        entry.button.removeAttribute('aria-busy');
        const purchaseScope = entry.button.closest('[data-av-purchase-state], [data-form-wrapper], product-form');
        const unavailable = purchaseScope?.matches(
          '[data-av-purchase-state="out_of_stock"], [data-av-purchase-state="unavailable"], .variant--soldout, .variant--unavailable'
        );
        if (!unavailable) entry.button.disabled = false;
        const label = labelNode(entry.button);
        if (label && entry.labelHTML != null) label.innerHTML = entry.labelHTML;
        else if (label && entry.label) label.textContent = entry.label;
        if (state.current === entry) {
          state.current = null;
          state.pendingVariant = '';
        }
      }, delay);
      return {
        begin: (button, form) => {
          if (!button) return;
          if (button.dataset.motionCartState === 'loading' && state.current?.button === button) return;
          if (state.current && state.current.button !== button) reset(state.current);
          const token = ++this.cartToken;
          const label = labelNode(button);
          const original = this.cartOriginalLabels.get(button);
          const input = form?.querySelector('[name="id"]') || button.closest('form')?.querySelector('[name="id"]');
          const entry = {
            token,
            button,
            label: original?.text || label?.textContent?.trim() || button.textContent.trim(),
            labelHTML: original?.html ?? label?.innerHTML,
            variant: input?.value || '',
            timer: null
          };
          state.current = entry;
          state.pendingVariant = entry.variant;
          button.dataset.motionCartState = 'loading';
          button.setAttribute('aria-busy', 'true');
          // The existing cart controller owns button disabling. It listens to
          // the same `theme:cart:add` event and deliberately ignores disabled
          // buttons, so disabling here can prevent the form post entirely when
          // this motion listener runs first.
          if (label) label.textContent = 'Adicionando…';
          entry.timer = wait(() => {
            if (state.current !== entry || entry.button.dataset.motionCartState !== 'loading') return;
            this.announce('A resposta demorou mais do que o esperado. Verifique a sacola ou tente novamente.');
            reset(entry);
          }, 10000);
        },
        success: (button) => {
          const entry = state.current && (!button || state.current.button === button) ? state.current : null;
          if (!entry) return;
          clearTimer(entry);
          entry.button.dataset.motionCartState = 'success';
          entry.button.removeAttribute('aria-busy');
          const label = labelNode(entry.button);
          if (label) label.textContent = 'Adicionado à sacola';
          this.announce('Produto adicionado à sacola.');
          reset(entry, 1200);
          wait(() => this.highlightNewLine(state.pendingVariant), 80);
        },
        error: (button, message) => {
          const entry = state.current && (!button || state.current.button === button)
            ? state.current
            : (button ? {
              button,
              label: labelNode(button)?.textContent?.trim() || button.textContent.trim(),
              labelHTML: labelNode(button)?.innerHTML,
              timer: null
            } : null);
          if (!entry) return;
          clearTimer(entry);
          entry.button.dataset.motionCartState = 'error';
          entry.button.removeAttribute('aria-busy');
          const label = labelNode(entry.button);
          if (label) label.textContent = 'Tente novamente';
          this.announce(message || 'Não foi possível adicionar o produto. Tente novamente.');
          reset(entry, 1800);
        },
        count: (count) => {
          if (!Number.isFinite(count) || count === state.previousCount) return;
          state.previousCount = count;
          qsa(document, 'cart-count,[data-cart-count]').forEach((node) => {
            node.dataset.motionCountFeedback = 'true';
            wait(() => delete node.dataset.motionCountFeedback, 380);
          });
        }
      };
    }

    highlightNewLine(variantId) {
      const exact = variantId ? document.querySelector(`[data-motion-cart-line][data-variant-id="${CSS.escape(String(variantId))}"]`) : null;
      const line = exact || document.querySelector('.drawer--cart [data-motion-cart-line],cart-drawer [data-motion-cart-line]');
      if (!line) return;
      line.dataset.motionNewLine = 'true';
      wait(() => delete line.dataset.motionNewLine, 850);
    }

    teardown(root) {
      qsa(root, '[data-motion-reveal]').forEach((element) => this.revealObserver?.unobserve(element));
      qsa(root, '[data-motion-hero]').forEach((hero) => {
        this.heroObserver?.unobserve(hero);
        this.heroAnimations.get(hero)?.cancel();
        this.heroAnimations.delete(hero);
      });
    }

    bindGlobalEvents() {
      document.addEventListener('submit', (event) => {
        const form = event.target;
        const button = form?.querySelector?.('[data-add-to-cart],[data-av-card11-submit]');
        if (button) this.rememberCartLabel(button, true);
      }, true);
      document.addEventListener('theme:cart:add', (event) => {
        const button = event.detail?.button;
        if (button) this.cart.begin(button, button.form || button.closest('form'));
      });
      document.addEventListener('pointerdown', (event) => {
        const control = event.target.closest?.('[data-motion-press]');
        if (control && (event.pointerType !== 'mouse' || event.button === 0)) {
          if (control.matches('[data-add-to-cart],[data-av-card11-submit]')) this.rememberCartLabel(control, true);
          control.dataset.motionPressed = 'true';
        }
      }, { passive: true });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((name) => document.addEventListener(name, (event) => {
        const control = event.target.closest?.('[data-motion-press]');
        if (control) delete control.dataset.motionPressed;
      }, { passive: true }));
      document.addEventListener('theme:media:select', (event) => this.onMediaSelect(event), true);
      document.addEventListener('theme:product:add-error', (event) => this.cart.error(event.detail?.button, event.detail?.response?.description));
      document.addEventListener('theme:cart:error', (event) => this.cart.error(null, event.detail?.description || event.detail?.message));
      document.addEventListener('theme:product:add', (event) => this.cart.success(event.detail?.button));
      document.addEventListener('theme:product:added', () => this.cart.success());
      document.addEventListener('av:cart:add', () => this.cart.success());
      document.addEventListener('theme:cart:change', (event) => this.cart.count(Number(event.detail?.cartCount)));
      document.addEventListener('visibilitychange', () => {
        document.body?.toggleAttribute('data-av-horizon-paused', document.hidden);
        this.heroAnimations.forEach((animation, hero) => {
          if (document.hidden) animation.pause();
          else if (hero.getBoundingClientRect().bottom > 0 && hero.getBoundingClientRect().top < innerHeight) animation.play();
        });
      });
      document.addEventListener('click', (event) => {
        const filter = event.target.closest?.('[data-filter-param-name],[data-filter-update-url],[data-tag-filter]');
        if (!filter || this.reduced.matches) return;
        const grid = document.querySelector('[data-collection-products],[data-product-grid],.collection__products');
        if (!grid) return;
        grid.classList.add('is-av-refreshing');
        wait(() => grid.classList.remove('is-av-refreshing'), 700);
      });
      document.addEventListener('shopify:section:load', (event) => this.init(event.target));
      document.addEventListener('shopify:section:unload', (event) => this.teardown(event.target));
      document.addEventListener('shopify:block:select', (event) => this.init(event.target));
      document.addEventListener('av:motion:refresh', (event) => this.init(event.detail?.root || document));
      const preferenceChange = () => this.reduced.matches ? this.finishAll(document) : this.init(document);
      this.reduced.addEventListener?.('change', preferenceChange);
      this.reduced.addListener?.(preferenceChange);
    }
  }

  const boot = () => { window.avMotion = window.avMotion || new AVMotionCraft(); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot, { once: true }) : boot();
})();
